import { useEffect, useRef, useState } from "react";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";

// @mui icons

// Material Dashboard 2 React components
import LoadingButton from "components/LoadingButton";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";

// Authentication layout components
import BasicLayoutLanding from "layouts/authentication/components/BasicLayoutLanding";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpg";

import { useAuth } from "contexts/auth.context";
import AuthService from "services/auth-service";

import ReCAPTCHA from "react-google-recaptcha";

import OTPDialog from "./OTPDialog";
import { useLanguage } from "contexts/language.context";
import { DEVELOPMENT } from "config";

function Login() {
  const authContext = useAuth();

  const recaptchaRef = useRef();

  const { label } = useLanguage();

  const [user, setUser] = useState({});
  const [credentialsErros, setCredentialsError] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [otpDialogInfo, setOtpDialogInfo] = useState(false);

  const [inputs, setInputs] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({
    emailError: false,
    passwordError: false,
  });

  const [loading, setLoading] = useState({
    login: false,
  });

  const addUserHandler = (newUser) => setUser(newUser);

  const handleSetRememberMe = () => {
    setRememberMe(!rememberMe);
  };

  const changeHandler = (e) => {
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value,
    });
  };

  const submitHandler = async (e) => {
    setLoading({ login: true });
    // check rememeber me?
    e.preventDefault();

    const mailFormat = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

    if (
      inputs.email.trim().length === 0 /*||
      !inputs.email.trim().match(mailFormat)*/
    ) {
      setErrors({ ...errors, emailError: true });
      return;
    }

    if (inputs.password.trim().length < 6) {
      setErrors({ ...errors, passwordError: true });
      return;
    }

    const newUser = { email: inputs.email, password: inputs.password };
    addUserHandler(newUser);

    const myData = {
      data: {
        type: "token",
        attributes: { ...newUser },
      },
    };

    recaptchaRef.current.reset();
    let response, data;
    try {
      if (!DEVELOPMENT) {
        const captchaValue = await recaptchaRef.current.executeAsync();
        if (!captchaValue) {
          alert("Please verify the reCAPTCHA!");
        } else {
          [response, data] = await AuthService.verifyRecaptcha(captchaValue);
          if (!response.ok || !data.success) {
            setLoading({ login: false });
            return;
          }
        }
      }

      [response, data] = await AuthService.login(myData);
      if (response.ok) {
        if (rememberMe) {
          localStorage.setItem(
            "rememberMe",
            JSON.stringify({ email: inputs.email })
          );
        } else {
          localStorage.removeItem("rememberMe");
        }
        if (data.token_type === "Speakeasy") {
          setOtpDialogInfo(true);
        } else if (data.token_type === "Bearer") {
          authContext.login(data.access_token, data.refresh_token);
        } else {
          setCredentialsError(data.errors?.[0]?.detail);
        }
      } else {
        setCredentialsError(data.errors?.[0]?.detail);
      }
    } catch (res) {
      if (res.hasOwnProperty("message")) {
        setCredentialsError(res.message);
      } else {
        setCredentialsError(res.errors?.[0]?.detail || res.data[0].detail);
      }
    }

    setLoading({ login: false });

    return () => {
      setInputs({
        email: "",
        password: "",
      });

      setErrors({
        emailError: false,
        passwordError: false,
      });
    };
  };

  useEffect(() => {
    const savedRememberMe = localStorage.getItem("rememberMe");
    if (savedRememberMe) {
      const email = JSON.parse(savedRememberMe)?.email;
      setInputs({ ...inputs, email });
    }
    setRememberMe(Boolean(savedRememberMe));
  }, []);

  return (
    <BasicLayoutLanding image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={3}
          pb={4}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {label("login-greeting")}
          </MDTypography>
          {/*<Grid
            container
            spacing={3}
            justifyContent="center"
            sx={{ mt: 1, mb: 2 }}
          >
            <Grid item xs={2}>
              <MDTypography
                component={MuiLink}
                href="#"
                variant="body1"
                color="white"
              >
                <FacebookIcon color="inherit" />
              </MDTypography>
            </Grid>
            <Grid item xs={2}>
              <MDTypography
                component={MuiLink}
                href="#"
                variant="body1"
                color="white"
              >
                <GitHubIcon color="inherit" />
              </MDTypography>
            </Grid>
            <Grid item xs={2}>
              <MDTypography
                component={MuiLink}
                href="#"
                variant="body1"
                color="white"
              >
                <GoogleIcon color="inherit" />
              </MDTypography>
            </Grid>
          </Grid>*/}
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox
            component="form"
            role="form"
            method="POST"
            onSubmit={submitHandler}
          >
            <MDBox mb={2}>
              <MDInput
                id="email-input"
                type="email"
                label={label("email-singular")}
                fullWidth
                value={inputs.email}
                name="email"
                onChange={changeHandler}
                error={errors.emailError}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                id="password-input"
                type="password"
                label={label("password-singular")}
                fullWidth
                name="password"
                value={inputs.password}
                onChange={changeHandler}
                error={errors.passwordError}
              />
            </MDBox>
            <MDBox display="flex" alignItems="center" ml={-1}>
              <Switch
                id="rememberMe-switch"
                checked={rememberMe}
                onChange={handleSetRememberMe}
              />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;{label("remember-me")}
              </MDTypography>
            </MDBox>
            <ReCAPTCHA
              ref={recaptchaRef}
              size="invisible"
              sitekey={process.env.REACT_APP_INVISIBLE_RECAPTCHAV2_SITE_KEY}
            />
            <MDBox mt={4} mb={1}>
              <LoadingButton
                loading={loading.login}
                variant="gradient"
                color="info"
                fullWidth
                type="submit"
              >
                {label("login-button")}
              </LoadingButton>
            </MDBox>
            {credentialsErros && (
              <MDTypography variant="caption" color="error" fontWeight="light">
                {credentialsErros}
              </MDTypography>
            )}
            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography
                variant="button"
                color="text"
                component={Link}
                to="/auth/forgot-password"
              >
                {label("forgot-password-link")}
              </MDTypography>
            </MDBox>
            {/* <MDBox mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Don&apos;t have an account?{" "}
                <MDTypography
                  component={Link}
                  to="/auth/register"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  Sign up
                </MDTypography>
              </MDTypography>
            </MDBox>*/}
          </MDBox>
        </MDBox>
      </Card>
      <OTPDialog
        isOpen={otpDialogInfo}
        email={inputs.email}
        password={inputs.password}
        setOtpDialogInfo={setOtpDialogInfo}
      />
    </BasicLayoutLanding>
  );
}

export default Login;
